import React, { useState, useEffect } from 'react';
import { Form, Button, Card, Container, Spinner, Image } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../firebase/config';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import Toast from '../../components/Toast';
import { uploadImage, deleteImage } from '../../services/cloudinaryService';

const Payments = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [paypalEnabled, setPaypalEnabled] = useState(false);
  const [wechatPayEnabled, setWechatPayEnabled] = useState(false);
  const [cashOnDelivery, setCashOnDelivery] = useState(false);
  const [wechatPaymentId, setWechatPaymentId] = useState('');
  const [wechatQrUrl, setWechatQrUrl] = useState('');
  const [wechatQrPublicId, setWechatQrPublicId] = useState('');
  const [cnyRate, setCnyRate] = useState('7.80');
  const [uploadingQr, setUploadingQr] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');

  useEffect(() => {
    const loadPaymentSettings = async () => {
      if (!currentUser) {
        navigate('/login');
        return;
      }

      try {
        setLoading(true);
        const shopRef = doc(db, 'shops', currentUser.uid);
        const shopDoc = await getDoc(shopRef);

        if (shopDoc.exists()) {
          const shopData = shopDoc.data();
          const payments = shopData.payments || {};
          setPaypalEnabled(payments.paypal || false);
          setWechatPayEnabled(payments.wechatPay || false);
          setCashOnDelivery(payments.cashOnDelivery || false);
          setWechatPaymentId(payments.wechatPaymentId || '');
          setWechatQrUrl(payments.wechatQrUrl || '');
          setWechatQrPublicId(payments.wechatQrPublicId || '');
          setCnyRate(payments.cnyRate || '7.80');
        }
      } catch (err) {
        console.error('Errore nel caricamento delle impostazioni di pagamento:', err);
        setToastMessage('Errore nel caricamento delle impostazioni');
        setToastVariant('danger');
        setShowToast(true);
      } finally {
        setLoading(false);
      }
    };

    loadPaymentSettings();
  }, [currentUser, navigate]);

  const handleQrUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      setToastMessage('L\'immagine non deve superare i 2MB');
      setToastVariant('danger');
      setShowToast(true);
      return;
    }

    try {
      setUploadingQr(true);
      const result = await uploadImage(file);
      // Elimina precedente QR se presente
      if (wechatQrPublicId) {
        await deleteImage(wechatQrPublicId).catch(() => {});
      }
      setWechatQrUrl(result.url);
      setWechatQrPublicId(result.publicId);
    } catch (err) {
      console.error('Errore upload QR code:', err);
      setToastMessage('Errore nel caricamento del QR code');
      setToastVariant('danger');
      setShowToast(true);
    } finally {
      setUploadingQr(false);
    }
  };

  const handleRemoveQr = async () => {
    if (wechatQrPublicId) {
      await deleteImage(wechatQrPublicId).catch(() => {});
    }
    setWechatQrUrl('');
    setWechatQrPublicId('');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const shopRef = doc(db, 'shops', currentUser.uid);
      await updateDoc(shopRef, {
        payments: {
          paypal: paypalEnabled,
          wechatPay: wechatPayEnabled,
          cashOnDelivery,
          wechatPaymentId,
          wechatQrUrl,
          wechatQrPublicId,
          cnyRate
        },
        updatedAt: new Date().toISOString()
      });

      setToastMessage('Impostazioni di pagamento salvate con successo');
      setToastVariant('success');
      setShowToast(true);
    } catch (err) {
      console.error('Errore nel salvataggio delle impostazioni di pagamento:', err);
      setToastMessage('Errore durante il salvataggio');
      setToastVariant('danger');
      setShowToast(true);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status" />
      </div>
    );
  }

  return (
    <Container className="py-4 d-flex justify-content-center">
      <Toast
        show={showToast}
        onClose={() => setShowToast(false)}
        message={toastMessage}
        variant={toastVariant}
      />
      <Card className="p-4 shadow-sm border-0 rounded-4" style={{ maxWidth: 600, width: '100%' }}>
        <Card.Body>
          <h4 className="fw-bold mb-4">{t('payment.settings_title')}</h4>
          <p className="text-muted mb-4">{t('payment.settings_description')}</p>

          <Form onSubmit={handleSave}>
            {/* PayPal */}
            <div className="d-flex align-items-center justify-content-between mb-4 p-3 rounded-3" style={{ backgroundColor: '#FFF8E7' }}>
              <div className="d-flex align-items-center gap-3">
                <div
                  className="rounded-3 d-flex align-items-center justify-content-center"
                  style={{ width: 48, height: 48, backgroundColor: '#FFC439' }}
                >
                  <i className="bi bi-paypal fs-4" style={{ color: '#003087' }}></i>
                </div>
                <div>
                  <h6 className="mb-0 fw-bold text-dark">PayPal</h6>
                  <small className="text-muted">{t('payment.paypal_description')}</small>
                </div>
              </div>
              <Form.Check
                type="switch"
                id="paypal-switch"
                checked={paypalEnabled}
                onChange={(e) => setPaypalEnabled(e.target.checked)}
              />
            </div>

            {/* Contrassegno */}
            <div className="d-flex align-items-center justify-content-between mb-4 p-3 rounded-3" style={{ backgroundColor: '#F0F0F0' }}>
              <div className="d-flex align-items-center gap-3">
                <div
                  className="rounded-3 d-flex align-items-center justify-content-center"
                  style={{ width: 48, height: 48, backgroundColor: '#6C757D' }}
                >
                  <i className="bi bi-cash-stack fs-4 text-white"></i>
                </div>
                <div>
                  <h6 className="mb-0 fw-bold text-dark">Pagamento alla consegna</h6>
                  <small className="text-muted">Il cliente paga in contanti al ricevimento dell'ordine</small>
                </div>
              </div>
              <Form.Check
                type="switch"
                id="cod-switch"
                checked={cashOnDelivery}
                onChange={(e) => setCashOnDelivery(e.target.checked)}
              />
            </div>

            {/* WeChat Pay */}
            <div className="d-flex align-items-center justify-content-between mb-4 p-3 rounded-3" style={{ backgroundColor: '#E8F5E9' }}>
              <div className="d-flex align-items-center gap-3">
                <div
                  className="rounded-3 d-flex align-items-center justify-content-center"
                  style={{ width: 48, height: 48, backgroundColor: '#07C160' }}
                >
                  <i className="bi bi-wechat fs-4 text-white"></i>
                </div>
                <div>
                  <h6 className="mb-0 fw-bold text-dark">WeChat Pay</h6>
                  <small className="text-muted">{t('payment.wechat_description')}</small>
                </div>
              </div>
              <Form.Check
                type="switch"
                id="wechat-switch"
                checked={wechatPayEnabled}
                onChange={(e) => setWechatPayEnabled(e.target.checked)}
              />
            </div>

            {/* Impostazioni WeChat Pay (visibili solo se abilitato) */}
            {wechatPayEnabled && (
              <div className="mb-4 p-3 rounded-3" style={{ backgroundColor: '#FAFAFA', border: '1px solid #E0E0E0' }}>
                <h6 className="fw-bold mb-3">Configurazione WeChat Pay</h6>

                {/* Upload QR Code */}
                <Form.Group className="mb-3" controlId="wechatQr">
                  <Form.Label className="small fw-medium">{t('payment.qr_upload')}</Form.Label>
                  <div className="d-flex align-items-start gap-3">
                    <div
                      className="rounded-3 bg-white d-flex align-items-center justify-content-center"
                      style={{ width: 100, height: 100, border: '2px dashed #ddd', flexShrink: 0 }}
                    >
                      {wechatQrUrl ? (
                        <Image src={wechatQrUrl} alt="WeChat QR" width={96} height={96} style={{ objectFit: 'contain' }} />
                      ) : (
                        <i className="bi bi-qr-code" style={{ fontSize: 40, color: '#ccc' }}></i>
                      )}
                    </div>
                    <div>
                      <Form.Control
                        type="file"
                        accept="image/*"
                        onChange={handleQrUpload}
                        disabled={uploadingQr}
                        size="sm"
                      />
                      {uploadingQr && (
                        <div className="mt-1">
                          <Spinner animation="border" size="sm" className="me-1" />
                          <small className="text-muted">Caricamento...</small>
                        </div>
                      )}
                      {wechatQrUrl && (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          className="mt-1 rounded-pill"
                          onClick={handleRemoveQr}
                        >
                          <i className="bi bi-trash me-1"></i>Rimuovi
                        </Button>
                      )}
                      <Form.Text className="text-muted d-block mt-1">
                        Carica il QR code del tuo account WeChat Pay
                      </Form.Text>
                    </div>
                  </div>
                </Form.Group>

                {/* ID WeChat per pagamento */}
                <Form.Group className="mb-3" controlId="wechatPaymentId">
                  <Form.Label className="small fw-medium">ID WeChat per pagamento</Form.Label>
                  <Form.Control
                    type="text"
                    value={wechatPaymentId}
                    onChange={e => setWechatPaymentId(e.target.value)}
                    placeholder="ID WeChat da mostrare al cliente per il pagamento"
                    size="sm"
                    style={{ maxWidth: 300 }}
                  />
                  <Form.Text className="text-muted">
                    Se vuoto, verrà usato l'ID WeChat delle impostazioni negozio
                  </Form.Text>
                </Form.Group>

                {/* Tasso di cambio CNY */}
                <Form.Group className="mb-2" controlId="cnyRate">
                  <Form.Label className="small fw-medium">{t('payment.cny_rate_label')}</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={cnyRate}
                    onChange={e => setCnyRate(e.target.value)}
                    placeholder="7.80"
                    size="sm"
                    style={{ maxWidth: 200 }}
                  />
                  <Form.Text className="text-muted">
                    Quanti CNY vale 1 EUR
                  </Form.Text>
                </Form.Group>
              </div>
            )}

            <div className="d-flex gap-2">
              <Button
                variant="outline-secondary"
                className="w-50 rounded-pill"
                onClick={() => navigate('/seller')}
                disabled={saving}
              >
                {t('common.cancel')}
              </Button>
              <Button
                type="submit"
                variant="primary"
                className="w-50 rounded-pill"
                disabled={saving}
              >
                {saving ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  t('common.save')
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default Payments;
